import React, { useEffect, useMemo, useState, useId, useCallback } from 'react';
import {
    Portal,
    TextField,
    FormControlLabel,
    Switch,
    Checkbox,
    IconButton,
    CircularProgress,
    Tooltip,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
} from '@mui/material';
import CancelIcon from '@mui/icons-material/Cancel';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import AddCardIcon from '@mui/icons-material/AddCard';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import SaveIcon from '@mui/icons-material/Save';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import Swal from 'sweetalert2';
import { useTranslation } from 'react-i18next';
import CustomDatePicker from '../../Widgets/CustomDates/CustomDatePicker/CustomDatePicker';
import { getVatValue } from '../../../Utils/GetVatValue';
import { buildPerClientStateFromSelection } from '../../../Utils/masavBulkReceiptGrouping';
import clientSavedCardsService from '../../../Services/clientSavedCards.service';
import s from './MasavBulkReceiptModal.module.scss';

/** Matches collection payment type enum: bank transfer (2) | credit card (0). */
const BANK_TRANSFER_TYPE = '2';
const CREDIT_CARD_TYPE = '0';

/** Payment-method values shared with the parent (CollectionPage) submit handler. */
export const MasavPaymentMethods = {
    bank: 'bank',
    creditCard: 'creditCard',
};

const fieldSx = {
    height: '40px',
    padding: '0 12px',
    borderRadius: '10px',
    background: '#f5f6fd',
    width: '100%',
    border: '1px solid #e5e7f6',
    fontSize: '0.875rem',
    boxSizing: 'border-box',
    transition: 'border-color .15s ease, box-shadow .15s ease, background .15s ease',
    '&:hover': { borderColor: '#c9cff5' },
    '&.Mui-focused, &:focus-within': {
        borderColor: '#304FFF',
        boxShadow: '0 0 0 3px rgba(48, 79, 255, 0.12)',
        background: '#fff',
    },
};

const datePickerInputSx = {
    ...fieldSx,
    padding: '0 9px',
};

const baseInputProps = (direction) => ({
    disableUnderline: true,
    className: `custom-input-box-sizing-toggle ${direction === 'ltr' ? 'ltr-input' : 'rtl-input'}`,
    sx: fieldSx,
});

const baseInputStyle = (direction) => ({
    style: { textAlign: direction === 'ltr' ? 'left' : 'right' },
});

function formatMoney(v, lang) {
    return Number(v || 0).toLocaleString(['he', 'ar'].includes(lang) ? 'he-IL' : 'en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    });
}

/** Same add-mode VAT UX as `AddNewCollectionItem` (amount in field = net when “including VAT”). */
function VatToggleAdd({ amountStr, priceIncludingVAT, vatRate, onChangeIncluding, t, lang, disabled }) {
    const raw = parseFloat(amountStr) || 0;
    const includes = priceIncludingVAT ?? true;
    const net = raw;
    const gross = raw * (1 + vatRate);

    const options = [
        { value: true, title: t('PriceIncludingVAT'), saved: net },
        { value: false, title: t('PriceExcludingVAT'), saved: gross },
    ];

    return (
        <div className={s.vatOptions} role="radiogroup" aria-label={t('VATOption')}>
            {options.map((o) => {
                const active = includes === o.value;
                return (
                    <button
                        key={String(o.value)}
                        type="button"
                        role="radio"
                        aria-checked={active}
                        disabled={disabled}
                        className={`${s.vatOption}${active ? ` ${s.vatOptionActive}` : ''}`}
                        onClick={() => onChangeIncluding(o.value)}
                    >
                        <span className={s.radioDot} />
                        <span className={s.vatOptionBody}>
                            <span className={s.vatOptionTitle}>{o.title}</span>
                            <span className={s.vatOptionValue}>
                                {t('PriceIncludingVAT')}: {formatMoney(o.saved, lang)}
                            </span>
                        </span>
                    </button>
                );
            })}
        </div>
    );
}

/** Labeled form cell inside a `.grid`; `full` spans both columns. */
const Field = ({ title, labelId, htmlFor, required = false, full = false, hint, children }) => (
    <div className={`${s.field}${full ? ` ${s.fieldFull}` : ''}`}>
        {title && (
            <label className={s.fieldLabel} id={labelId} htmlFor={htmlFor}>
                {title}
                {required && <span className={s.required}>*</span>}
            </label>
        )}
        {children}
        {hint && <span className={s.fieldHint}>{hint}</span>}
    </div>
);

/**
 * Record one shared bank payment per selected client (MASAV rows grouped by client) and optionally generate receipt drafts.
 * Mirrors payment + VAT amount rules from AddNewCollectionItem (add / multiple path).
 */
const MasavBulkReceiptModal = ({
    open,
    onClose,
    selectedRows = [],
    direction: directionProp,
    submitting = false,
    onSubmit,
    /** When true, exposes the "Credit card" payment method toggle. */
    enableCreditCard = true,
    /** Pre-fills the shared payment description when the modal opens. */
    defaultDescription = '',
    /** Per-row charge progress map (caseId → 'pending'|'success'|'failed'|null). */
    chargeProgress = null,
    /** Opens the SavedCardsModal scoped to a single client (when user clicks "Save card"). */
    onOpenSavedCardsFor,
}) => {
    const { t, i18n } = useTranslation();
    const direction = i18n.dir() || directionProp || 'ltr';
    const isRtl = direction === 'rtl';
    const formId = useId();
    const paymentDateLabelId = `${formId}-payment-date`;
    const descId = `${formId}-description`;
    const refId = `${formId}-reference`;

    const [paymentDate, setPaymentDate] = useState(() => new Date());
    const [description, setDescription] = useState('');
    const [reference, setReference] = useState('');
    const [priceIncludingVAT, setPriceIncludingVAT] = useState(true);
    const [perClient, setPerClient] = useState([]);
    /** When true, each payment uses the client's own charge description(s) instead of the shared one. */
    const [useItemDescriptions, setUseItemDescriptions] = useState(false);

    // Payment-method state: 'bank' (existing default) or 'creditCard' (new).
    const [paymentMethod, setPaymentMethod] = useState(MasavPaymentMethods.bank);

    // Saved-card map: caseId → ClientSavedCardRowDto (or null). Loaded on demand.
    const [savedCardsByCase, setSavedCardsByCase] = useState({});
    const [loadingSavedCards, setLoadingSavedCards] = useState(false);

    const vatRate = useMemo(() => getVatValue(paymentDate), [paymentDate]);

    useEffect(() => {
        if (!open || !selectedRows?.length) return;
        setPaymentDate(new Date());
        setDescription(defaultDescription || '');
        setReference('');
        setPriceIncludingVAT(true);
        setPaymentMethod(MasavPaymentMethods.bank);
        setSavedCardsByCase({});
        setUseItemDescriptions(false);
        setPerClient(buildPerClientStateFromSelection(selectedRows));
    }, [open, selectedRows, defaultDescription]);

    // Fetch saved cards once when the user switches to credit-card mode (and whenever the modal opens in CC).
    const fetchSavedCards = useCallback(async () => {
        if (!open || paymentMethod !== MasavPaymentMethods.creditCard) return;
        setLoadingSavedCards(true);
        try {
            const rows = await clientSavedCardsService.list({ leadingBusiness: true });
            const byCase = {};
            (Array.isArray(rows) ? rows : []).forEach((r) => { byCase[r.caseId] = r; });
            setSavedCardsByCase(byCase);
        } catch {
            setSavedCardsByCase({});
        } finally {
            setLoadingSavedCards(false);
        }
    }, [open, paymentMethod]);

    useEffect(() => { fetchSavedCards(); }, [fetchSavedCards]);

    const L = baseInputProps(direction);
    const IS = baseInputStyle(direction);

    const sampleAmountForVat = useMemo(() => {
        const includedRows = perClient.filter((r) => r.include);
        const source = includedRows.length ? includedRows : perClient;
        const total = source.reduce((sum, row) => sum + (parseFloat(String(row.amount ?? 0)) || 0), 0);
        return String(total);
    }, [perClient]);

    const isCreditCardMode = paymentMethod === MasavPaymentMethods.creditCard;

    /** At least one selected client has its own charge description → the per-item toggle is useful. */
    const anyItemDescription = useMemo(
        () => perClient.some((r) => (r.itemDescription || '').trim()),
        [perClient]
    );

    const validationError = useMemo(() => {
        const includedRowsList = perClient.filter((r) => r.include);
        const allIncludedHaveItemDesc =
            includedRowsList.length > 0 &&
            includedRowsList.every((r) => (r.itemDescription || '').trim());
        // Shared description stays required unless every included client has its own item description.
        if (!description?.trim() && !(useItemDescriptions && allIncludedHaveItemDesc)) {
            return t('MasavBulkReceiptErrDescription');
        }
        const includedWithAmount = perClient.filter((r) => r.include && (parseFloat(r.amount) || 0) > 0);
        if (!includedWithAmount.length) return t('MasavBulkReceiptErrAmount');
        if (isCreditCardMode) {
            const missingCard = includedWithAmount.find((r) => !savedCardsByCase[r.caseId]?.savedCardId);
            if (missingCard) {
                return t('MasavBulkReceiptErrMissingCard', {
                    name: missingCard.clientName || missingCard.caseId,
                });
            }
        }
        return null;
    }, [description, perClient, isCreditCardMode, savedCardsByCase, useItemDescriptions, t]);

    const updateRow = (caseId, patch) => {
        setPerClient((prev) => prev.map((r) => (r.caseId === caseId ? { ...r, ...patch } : r)));
    };

    const handlePaymentMethodChange = (_e, val) => {
        if (!val) return;
        setPaymentMethod(val);
        // When CC is chosen, auto-uncheck rows lacking a saved card so the
        // user sees an actionable starting point.
        if (val === MasavPaymentMethods.creditCard) {
            setPerClient((prev) => prev.map((r) => ({
                ...r,
                include: r.include && !!savedCardsByCase[r.caseId]?.savedCardId,
            })));
        }
    };

    const handleSubmit = async () => {
        if (validationError || submitting) return;

        const includedRows = perClient.filter((r) => r.include && (parseFloat(r.amount) || 0) > 0);
        const includedCaseIds = Array.from(new Set(includedRows.map((r) => String(r.caseId ?? ''))));

        if (isCreditCardMode) {
            // CC path: don't prompt for a receipt date — parent loops the
            // charge-saved-card endpoint per row (the server records the
            // CreditCard payment on each client's ledger on success).
            const rowsWithCard = includedRows.map((row) => ({
                ...row,
                savedCardId: savedCardsByCase[row.caseId]?.savedCardId,
                last4: savedCardsByCase[row.caseId]?.last4,
            }));
            onSubmit?.({
                paymentMethod: MasavPaymentMethods.creditCard,
                shared: {
                    type: CREDIT_CARD_TYPE,
                    date: paymentDate,
                    description: description.trim(),
                    reference: reference.trim(),
                    priceIncludingVAT,
                },
                useItemDescriptions,
                rows: rowsWithCard,
                selectedClientCount: includedCaseIds.length,
                totalClientCount: perClient.length,
                generateReceiptDraft: false,
                receiptDate: null,
            });
            return;
        }

        const result = await Swal.fire({
            title: '?' + t('GenerateReceipt'),
            width: 520,
            html: `
                <p style="margin: 0 0 0.5em; text-align: center; direction: ${direction};">${t('WouldYouLikeToGenerateAReceiptForSelectedPayments')}</p>
                <div style="margin-top: 15px; width: 100%; display: flex; justify-content: center; box-sizing: border-box;">
                    <div class="masav-bulk-receipt-swal-fields" style="width: 100%; max-width: 260px; box-sizing: border-box; direction: ${direction}; text-align: ${isRtl ? 'right' : 'left'};">
                        <label for="masav-bulk-receipt-date" style="display: block; margin-bottom: 8px; font-weight: bold; text-align: center;">${t('ReceiptDate')}:</label>
                        <input type="date" id="masav-bulk-receipt-date" class="swal2-input masav-bulk-receipt-date-input" style="max-width: 100%;" value="${new Date().toISOString().split('T')[0]}">
                    </div>
                </div>
            `,
            icon: 'question',
            showCancelButton: true,
            confirmButtonText: t('Yes'),
            cancelButtonText: t('No'),
            didOpen: () => {
                const htmlContainer = document.querySelector('.swal2-html-container');
                if (htmlContainer) {
                    htmlContainer.style.width = '100%';
                    htmlContainer.style.maxWidth = '100%';
                    htmlContainer.style.boxSizing = 'border-box';
                }
                const dateInput = document.getElementById('masav-bulk-receipt-date');
                if (dateInput) {
                    dateInput.style.setProperty('width', '100%', 'important');
                    dateInput.style.setProperty('max-width', '100%', 'important');
                    dateInput.style.setProperty('min-width', '0', 'important');
                    dateInput.style.setProperty('box-sizing', 'border-box', 'important');
                    dateInput.style.setProperty('margin-left', '0', 'important');
                    dateInput.style.setProperty('margin-right', '0', 'important');
                }
            },
            preConfirm: () => {
                const dateInput = document.getElementById('masav-bulk-receipt-date');
                const selectedDate = dateInput?.value;
                if (!selectedDate) {
                    Swal.showValidationMessage(t('PleaseSelectADate'));
                    return false;
                }
                return selectedDate;
            },
        });

        const generateReceiptDraft = Boolean(result.isConfirmed);
        const receiptDate = generateReceiptDraft ? result.value : null;

        onSubmit?.({
            paymentMethod: MasavPaymentMethods.bank,
            shared: {
                type: BANK_TRANSFER_TYPE,
                date: paymentDate,
                description: description.trim(),
                reference: reference.trim(),
                priceIncludingVAT,
                bankName: '',
                bankCode: '',
                bankBranchCode: '',
                supplierAccountNumber: '',
            },
            useItemDescriptions,
            rows: includedRows,
            selectedClientCount: includedCaseIds.length,
            totalClientCount: perClient.length,
            generateReceiptDraft,
            receiptDate,
        });
    };

    const titleId = `${formId}-title`;
    const descAriaId = `${formId}-desc`;

    const rowHasCard = (row) => !!savedCardsByCase[row.caseId]?.savedCardId;
    const isRowChecked = (row) => row.include && (!isCreditCardMode || rowHasCard(row));

    const summary = useMemo(() => {
        const acc = { checked: 0, ready: 0, sourceRows: 0, total: 0, missingCard: 0, done: 0, failed: 0, pending: 0 };
        perClient.forEach((row) => {
            const hasCard = !!savedCardsByCase[row.caseId]?.savedCardId;
            const checked = row.include && (!isCreditCardMode || hasCard);
            const amount = parseFloat(row.amount) || 0;
            acc.sourceRows += row.rowCount || 0;
            if (checked) {
                acc.checked += 1;
                if (amount > 0) {
                    acc.ready += 1;
                    acc.total += amount;
                }
            }
            if (isCreditCardMode && !hasCard) acc.missingCard += 1;
            const progress = chargeProgress ? chargeProgress[row.caseId] : null;
            if (progress === 'success') acc.done += 1;
            else if (progress === 'failed') acc.failed += 1;
            else if (progress === 'pending') acc.pending += 1;
        });
        return acc;
    }, [perClient, savedCardsByCase, isCreditCardMode, chargeProgress]);

    const toggleableRows = perClient.filter((r) => !isCreditCardMode || rowHasCard(r));
    const allToggleableChecked = toggleableRows.length > 0 && toggleableRows.every((r) => r.include);
    const someToggleableChecked = toggleableRows.some((r) => r.include);
    const hasProgress = summary.done + summary.failed + summary.pending > 0;

    const handleToggleAll = (checked) => {
        setPerClient((prev) => prev.map((r) => (
            !isCreditCardMode || savedCardsByCase[r.caseId]?.savedCardId ? { ...r, include: checked } : r
        )));
    };

    const handleClose = () => {
        if (submitting) return;
        onClose?.();
    };

    const getRowStatus = (row, hasCard, rowProgress) => {
        if (rowProgress === 'pending') {
            return { className: s.chip, label: t('Pending'), icon: <CircularProgress size={10} color="inherit" /> };
        }
        if (rowProgress === 'success') {
            return { className: `${s.chip} ${s.chipSuccess}`, label: t('Done'), icon: <CheckCircleIcon sx={{ fontSize: 14 }} /> };
        }
        if (rowProgress === 'failed') {
            return {
                className: `${s.chip} ${s.chipError}`,
                label: t('MasavBulkReceiptChargeFailedRow'),
                icon: <ErrorOutlineIcon sx={{ fontSize: 14 }} />,
            };
        }
        if (isCreditCardMode && !hasCard) {
            return { className: `${s.chip} ${s.chipWarning}`, label: t('SavedCards.noCard') };
        }
        if (!row.include) {
            return { className: `${s.chip} ${s.chipMuted}`, label: t('MasavBulkReceiptRowExcluded') };
        }
        if (!((parseFloat(row.amount) || 0) > 0)) {
            return { className: `${s.chip} ${s.chipError}`, label: t('MasavBulkReceiptRowNoAmount') };
        }
        return { className: `${s.chip} ${s.chipSuccess}`, label: t('MasavBulkReceiptRowReady') };
    };

    if (!open) return null;

    const lang = i18n.language;
    const colCount = 4 + (useItemDescriptions ? 1 : 0) + (isCreditCardMode ? 1 : 0);

    const renderPerClientTable = () => (
        <div className={s.tableWrap} dir={direction}>
            <Table size="small" stickyHeader>
                <TableHead>
                    <TableRow>
                        <TableCell sx={{ minWidth: 220 }}>
                            <span className={s.clientHead}>
                                <Checkbox
                                    size="small"
                                    checked={allToggleableChecked}
                                    indeterminate={someToggleableChecked && !allToggleableChecked}
                                    onChange={(e) => handleToggleAll(e.target.checked)}
                                    disabled={submitting || toggleableRows.length === 0}
                                    inputProps={{ 'aria-label': t('SelectAll') }}
                                />
                                {t('ClientName')}
                            </span>
                        </TableCell>
                        <TableCell className={s.num}>{t('MasavPayableAmount')}</TableCell>
                        <TableCell className={s.num} sx={{ width: 150 }}>{t('Amount')}</TableCell>
                        {useItemDescriptions && <TableCell>{t('Description')}</TableCell>}
                        {isCreditCardMode && <TableCell>{t('SavedCards.card')}</TableCell>}
                        <TableCell sx={{ width: 130 }}>{t('Status')}</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {perClient.length === 0 ? (
                        <TableRow>
                            <TableCell colSpan={colCount}>
                                <div className={s.emptyState}>{t('NoData')}</div>
                            </TableCell>
                        </TableRow>
                    ) : (
                        perClient.map((row) => {
                            const card = savedCardsByCase[row.caseId];
                            const hasCard = !!card?.savedCardId;
                            const rowProgress = chargeProgress ? chargeProgress[row.caseId] : null;
                            const canToggleInclude = !submitting && (!isCreditCardMode || hasCard);
                            const checked = isRowChecked(row);
                            const status = getRowStatus(row, hasCard, rowProgress);
                            const clientLabel = row.clientName || row.caseId;
                            const itemDesc = (row.itemDescription || '').trim();
                            const shownDesc = itemDesc || description.trim();
                            const rowClass = rowProgress === 'failed'
                                ? s.rowFailed
                                : rowProgress === 'success'
                                    ? s.rowSuccess
                                    : !checked
                                        ? s.rowExcluded
                                        : undefined;
                            return (
                                <TableRow key={row.caseId} className={rowClass}>
                                    <TableCell>
                                        <div className={s.clientCell}>
                                            <Checkbox
                                                size="small"
                                                checked={checked}
                                                onChange={(e) => updateRow(row.caseId, { include: e.target.checked })}
                                                disabled={!canToggleInclude}
                                                inputProps={{
                                                    'aria-label': t('MasavBulkReceiptIncludeClient', { name: clientLabel }),
                                                }}
                                            />
                                            <div className={s.clientText}>
                                                <span className={s.clientName}>{row.clientName}</span>
                                                <span className={s.clientCase}>{t('Case')}: {row.caseId}</span>
                                            </div>
                                        </div>
                                    </TableCell>
                                    <TableCell className={s.num}>
                                        {formatMoney(row.totalSavedAmountIncludesVAT, lang)}
                                        {row.rowCount > 1 && (
                                            <span className={s.rowsHint}>{row.rowCount} {t('Rows')}</span>
                                        )}
                                    </TableCell>
                                    <TableCell className={s.num}>
                                        <TextField
                                            type="number"
                                            variant="standard"
                                            className={s.amountField}
                                            value={row.amount}
                                            onChange={(e) => updateRow(row.caseId, { amount: e.target.value })}
                                            disabled={submitting || !row.include || (isCreditCardMode && !hasCard)}
                                            InputProps={{ disableUnderline: true }}
                                            inputProps={{
                                                min: 0,
                                                step: '0.01',
                                                'aria-label': `${t('Amount')} – ${clientLabel}`,
                                            }}
                                        />
                                    </TableCell>
                                    {useItemDescriptions && (
                                        <TableCell>
                                            <div className={s.descCell} title={shownDesc || undefined}>
                                                {shownDesc ? (
                                                    <span className={itemDesc ? undefined : s.descFallback}>{shownDesc}</span>
                                                ) : (
                                                    <span className={s.descFallback}>{t('MasavBulkReceiptNoItemDescription')}</span>
                                                )}
                                            </div>
                                        </TableCell>
                                    )}
                                    {isCreditCardMode && (
                                        <TableCell>
                                            <div className={s.cardCell}>
                                                {loadingSavedCards ? (
                                                    <CircularProgress size={14} className={s.spinner} />
                                                ) : hasCard ? (
                                                    <Tooltip title={t('SavedCards.last4')}>
                                                        <span className={`${s.chip} ${s.chipLtr}`}>
                                                            <CreditCardIcon sx={{ fontSize: 14 }} />
                                                            {`•••• ${card.last4}`}
                                                        </span>
                                                    </Tooltip>
                                                ) : (
                                                    <button
                                                        type="button"
                                                        className={`${s.secondaryBtn} ${s.smallBtn}`}
                                                        onClick={() => onOpenSavedCardsFor?.(row.caseId, row.clientName)}
                                                        disabled={submitting}
                                                    >
                                                        <AddCardIcon sx={{ fontSize: 16 }} />
                                                        {t('SavedCards.add')}
                                                    </button>
                                                )}
                                            </div>
                                        </TableCell>
                                    )}
                                    <TableCell>
                                        <span className={status.className} role="status">
                                            {status.icon}
                                            {status.label}
                                        </span>
                                    </TableCell>
                                </TableRow>
                            );
                        })
                    )}
                </TableBody>
            </Table>
        </div>
    );

    return (
        <Portal>
            <div className={`${s.overlay} ${isRtl ? s.rtl : s.ltr}`} onClick={handleClose}>
                <div
                    dir={direction}
                    onClick={(e) => e.stopPropagation()}
                    role="dialog"
                    aria-modal="true"
                    aria-labelledby={titleId}
                    aria-describedby={descAriaId}
                >
                    <div className={s.modal}>
                        <div className={s.body}>
                            <div className={s.header}>
                                <Tooltip title={t('Close')}>
                                    <span>
                                        <IconButton
                                            onClick={handleClose}
                                            disabled={submitting}
                                            className={s.closeIcon}
                                            aria-label={t('Close')}
                                        >
                                            <CancelIcon className={s.closeIcon} />
                                        </IconButton>
                                    </span>
                                </Tooltip>
                                <div className={s.headerTitle}>
                                    <span className={s.headerTitleRow} id={titleId}>
                                        <ReceiptLongIcon />
                                        {t('MasavBulkReceiptTitle')}
                                    </span>
                                    <span className={s.headerSubtitle} id={descAriaId}>
                                        {t('MasavBulkReceiptIntro')}
                                    </span>
                                </div>
                                <div className={s.headerSpacer} />
                            </div>

                            <div className={s.scroll}>
                                {enableCreditCard && (
                                    <div className={s.methodBar} role="radiogroup" aria-label={t('MasavBulkReceiptPaymentMethodLabel')}>
                                        <span className={s.fieldLabel}>{t('MasavBulkReceiptPaymentMethodLabel')}</span>
                                        {[
                                            { value: MasavPaymentMethods.bank, label: t('BankTransfer'), icon: <AccountBalanceIcon fontSize="small" /> },
                                            { value: MasavPaymentMethods.creditCard, label: t('CreditCard'), icon: <CreditCardIcon fontSize="small" /> },
                                        ].map((m) => {
                                            const active = paymentMethod === m.value;
                                            return (
                                                <button
                                                    key={m.value}
                                                    type="button"
                                                    role="radio"
                                                    aria-checked={active}
                                                    className={`${s.tab}${active ? ` ${s.tabActive}` : ''}`}
                                                    disabled={submitting}
                                                    onClick={() => { if (!active) handlePaymentMethodChange(null, m.value); }}
                                                >
                                                    {m.icon}
                                                    {m.label}
                                                </button>
                                            );
                                        })}
                                        {isCreditCardMode && loadingSavedCards && (
                                            <CircularProgress size={16} className={s.spinner} />
                                        )}
                                    </div>
                                )}

                                <div className={s.stats}>
                                    <div className={s.statCard}>
                                        <span className={s.statLabel}>{t('Clients')}</span>
                                        <span className={s.statValue}>{summary.checked} / {perClient.length}</span>
                                    </div>
                                    <div className={s.statCard}>
                                        <span className={s.statLabel}>{t('Rows')}</span>
                                        <span className={s.statValue}>{summary.sourceRows}</span>
                                    </div>
                                    <div className={`${s.statCard} ${s.statCardAccent}`}>
                                        <span className={s.statLabel}>
                                            {isCreditCardMode ? t('CreditCardBulkCharge.amountToCharge') : t('TotalPayments')}
                                        </span>
                                        <span className={s.statValue}>{formatMoney(summary.total, lang)}</span>
                                    </div>
                                    <div className={`${s.statCard} ${s.statCardSuccess}`}>
                                        <span className={s.statLabel}>{t('MasavBulkReceiptRowReady')}</span>
                                        <span className={s.statValue}>{summary.ready}</span>
                                    </div>
                                    {isCreditCardMode && summary.missingCard > 0 && (
                                        <div className={`${s.statCard} ${s.statCardError}`}>
                                            <span className={s.statLabel}>{t('MissingCard')}</span>
                                            <span className={s.statValue}>{summary.missingCard}</span>
                                        </div>
                                    )}
                                    {hasProgress && (
                                        <>
                                            <div className={`${s.statCard} ${s.statCardSuccess}`}>
                                                <span className={s.statLabel}>{t('Done')}</span>
                                                <span className={s.statValue}>{summary.done}</span>
                                            </div>
                                            <div className={`${s.statCard}${summary.failed > 0 ? ` ${s.statCardError}` : ''}`}>
                                                <span className={s.statLabel}>{t('Failed')}</span>
                                                <span className={s.statValue}>{summary.failed}</span>
                                            </div>
                                        </>
                                    )}
                                </div>

                                <div className={s.section}>
                                    <div className={s.sectionHeader}>
                                        <h3 className={s.sectionTitle}>{t('MasavBulkReceiptSharedSection')}</h3>
                                    </div>
                                    <div className={s.grid}>
                                        <Field title={t('PaymentType')}>
                                            <TextField
                                                value={isCreditCardMode ? t('CreditCard') : t('BankTransfer')}
                                                fullWidth
                                                disabled
                                                variant="standard"
                                                InputProps={L}
                                                inputProps={{
                                                    ...IS.style,
                                                    readOnly: true,
                                                    'aria-readonly': true,
                                                    'aria-label': t('PaymentType'),
                                                }}
                                                sx={{
                                                    '& .MuiInputBase-root.Mui-disabled': {
                                                        opacity: 1,
                                                        WebkitTextFillColor: 'inherit',
                                                    },
                                                }}
                                            />
                                        </Field>

                                        <Field title={t('PaymentDate')} labelId={paymentDateLabelId}>
                                            <CustomDatePicker
                                                date={paymentDate}
                                                onDateChange={(d) => d && setPaymentDate(d)}
                                                height="40px"
                                                inputSx={datePickerInputSx}
                                            />
                                        </Field>

                                        <Field
                                            title={t('Description')}
                                            htmlFor={descId}
                                            required={!useItemDescriptions}
                                            hint={useItemDescriptions ? t('MasavBulkReceiptSharedDescFallbackHint') : undefined}
                                        >
                                            <TextField
                                                id={descId}
                                                fullWidth
                                                variant="standard"
                                                value={description}
                                                onChange={(e) => setDescription(e.target.value)}
                                                disabled={submitting}
                                                InputProps={L}
                                                inputProps={{ ...IS.style, 'aria-required': !useItemDescriptions }}
                                            />
                                        </Field>

                                        <Field title={t('Reference')} htmlFor={refId}>
                                            <TextField
                                                id={refId}
                                                fullWidth
                                                variant="standard"
                                                value={reference}
                                                onChange={(e) => setReference(e.target.value)}
                                                disabled={submitting}
                                                InputProps={L}
                                                inputProps={IS}
                                            />
                                        </Field>
                                    </div>

                                    {anyItemDescription && (
                                        <div className={s.switchRow}>
                                            <FormControlLabel
                                                control={
                                                    <Switch
                                                        checked={useItemDescriptions}
                                                        onChange={(e) => setUseItemDescriptions(e.target.checked)}
                                                        disabled={submitting}
                                                        size="small"
                                                    />
                                                }
                                                label={<span className={s.switchLabel}>{t('MasavBulkReceiptUseItemDescriptions')}</span>}
                                                sx={{ m: 0 }}
                                            />
                                        </div>
                                    )}

                                    <div className={s.subTitle}>{t('VATOption')}</div>
                                    <VatToggleAdd
                                        amountStr={sampleAmountForVat}
                                        priceIncludingVAT={priceIncludingVAT}
                                        vatRate={vatRate}
                                        onChangeIncluding={setPriceIncludingVAT}
                                        t={t}
                                        lang={lang}
                                        disabled={submitting}
                                    />
                                    <div className={s.note}>
                                        <InfoOutlinedIcon sx={{ fontSize: 16 }} />
                                        {t('SavedAmountIncludesVAT')}
                                    </div>
                                </div>

                                <div className={s.section}>
                                    <div className={s.sectionHeader}>
                                        <h3 className={s.sectionTitle}>
                                            {t('MasavBulkReceiptPerClientSection')} ({perClient.length})
                                        </h3>
                                    </div>
                                    {renderPerClientTable()}
                                </div>
                            </div>

                            <div className={s.footer}>
                                {validationError ? (
                                    <span className={s.footerHint} role="status">
                                        <WarningAmberIcon sx={{ fontSize: 18 }} />
                                        {validationError}
                                    </span>
                                ) : (
                                    <span className={s.footerTotals}>
                                        {t('Clients')}: {summary.ready} · {formatMoney(summary.total, lang)}
                                    </span>
                                )}
                                <span className={s.footerSpacer} />
                                <button type="button" className={s.ghostBtn} onClick={handleClose} disabled={submitting}>
                                    {t('Cancel')}
                                </button>
                                <button
                                    type="button"
                                    className={s.primaryBtn}
                                    onClick={handleSubmit}
                                    disabled={!!validationError || submitting}
                                >
                                    {submitting ? (
                                        <CircularProgress color="inherit" size={16} />
                                    ) : isCreditCardMode ? (
                                        <CreditCardIcon fontSize="small" />
                                    ) : (
                                        <SaveIcon fontSize="small" />
                                    )}
                                    {isCreditCardMode ? t('MasavBulkReceiptChargeSelected') : t('MasavBulkReceiptSubmit')}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </Portal>
    );
};

export default MasavBulkReceiptModal;
